import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, 
  XCircle,
  Download, 
  Loader2, 
  RefreshCw,
  Package,
  IndianRupee
} from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

function LowStockReport() {
  var loadingState = React.useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var reportState = React.useState(null);
  var report = reportState[0];
  var setReport = reportState[1];
  
  var exportingState = React.useState(false);
  var exporting = exportingState[0];
  var setExporting = exportingState[1];

  React.useEffect(function() {
    fetchReport();
  }, []);

  function fetchReport() {
    setLoading(true);
    var token = localStorage.getItem('token');
    axios.get(API + '/inventory/low-stock-report', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setReport(response.data);
      setLoading(false);
    }).catch(function(error) {
      toast.error('Failed to fetch low stock report');
      setLoading(false);
    });
  }

  function exportReport() {
    setExporting(true);
    var token = localStorage.getItem('token');
    axios.post(API + '/export/inventory', { low_stock_only: true }, {
      headers: { Authorization: 'Bearer ' + token },
      responseType: 'blob'
    }).then(function(response) {
      var url = window.URL.createObjectURL(new Blob([response.data]));
      var link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Low_Stock_Report_' + new Date().toISOString().slice(0,10) + '.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report exported');
      setExporting(false);
    }).catch(function(error) {
      toast.error('Failed to export');
      setExporting(false);
    });
  }

  function formatCurrency(val) {
    return '₹' + (val || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  if (loading) {
    return React.createElement('div', { className: 'flex items-center justify-center h-64' },
      React.createElement(Loader2, { className: 'w-8 h-8 animate-spin text-blue-600' })
    );
  }

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'low-stock-report' },
    // Header
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('div', null,
        React.createElement('h1', { className: 'text-3xl font-heading font-bold text-primary' }, 'Low Stock Report'),
        React.createElement('p', { className: 'text-slate-600 mt-1' }, 'Items below reorder level requiring attention')
      ),
      React.createElement('div', { className: 'flex gap-2' },
        React.createElement(Button, { variant: 'outline', onClick: exportReport, disabled: exporting, className: 'gap-2' },
          exporting ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Download, { className: 'w-4 h-4' }),
          'Export Report'
        ),
        React.createElement(CsvDownloadButton, { onDownload: exportReport, testId: 'low-stock-export-csv', size: 'default', disabled: exporting }),
        React.createElement(Button, { variant: 'outline', onClick: fetchReport, className: 'gap-2' },
          React.createElement(RefreshCw, { className: 'w-4 h-4' }),
          'Refresh'
        )
      )
    ),

    // Summary Cards
    React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-4 gap-4' },
      React.createElement(Card, { className: 'bg-amber-50 border-amber-200' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center gap-3' },
            React.createElement(AlertTriangle, { className: 'w-8 h-8 text-amber-600' }),
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-amber-600' }, 'Low Stock Items'),
              React.createElement('p', { className: 'text-2xl font-bold text-amber-800' }, report?.low_stock_count || 0)
            )
          )
        )
      ),
      
      React.createElement(Card, { className: 'bg-red-50 border-red-200' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center gap-3' },
            React.createElement(XCircle, { className: 'w-8 h-8 text-red-600' }),
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-red-600' }, 'Out of Stock'),
              React.createElement('p', { className: 'text-2xl font-bold text-red-800' }, report?.out_of_stock_count || 0)
            )
          )
        )
      ),
      
      React.createElement(Card, { className: 'bg-blue-50 border-blue-200' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center gap-3' },
            React.createElement(Package, { className: 'w-8 h-8 text-blue-600' }),
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-blue-600' }, 'Total Items'),
              React.createElement('p', { className: 'text-2xl font-bold text-blue-800' }, report?.total_count || 0)
            )
          )
        )
      ),
      
      React.createElement(Card, { className: 'bg-purple-50 border-purple-200' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center gap-3' },
            React.createElement(IndianRupee, { className: 'w-8 h-8 text-purple-600' }),
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-purple-600' }, 'Reorder Value'),
              React.createElement('p', { className: 'text-2xl font-bold text-purple-800' }, formatCurrency(report?.total_reorder_value))
            )
          )
        )
      )
    ),

    // Items Table
    React.createElement(Card, null,
      React.createElement(CardHeader, null,
        React.createElement(CardTitle, null, 'Items Requiring Reorder')
      ),
      React.createElement(CardContent, null,
        (!report?.items || report.items.length === 0)
          ? React.createElement('div', { className: 'text-center py-12' },
              React.createElement('p', { className: 'text-green-600 font-semibold' }, '✓ All items are adequately stocked!'),
              React.createElement('p', { className: 'text-slate-500 mt-1' }, 'No items below minimum stock level')
            )
          : React.createElement('div', { className: 'overflow-x-auto' },
              React.createElement('table', { className: 'w-full text-sm' },
                React.createElement('thead', null,
                  React.createElement('tr', { className: 'border-b bg-slate-50' },
                    React.createElement('th', { className: 'text-left p-3 font-semibold' }, 'Product'),
                    React.createElement('th', { className: 'text-left p-3 font-semibold' }, 'Brand / Category'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Current Stock'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Min Level'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Shortage'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Purchase Rate'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Reorder Value'),
                    React.createElement('th', { className: 'text-center p-3 font-semibold' }, 'Status')
                  )
                ),
                React.createElement('tbody', null,
                  report.items.map(function(item) {
                    var isOutOfStock = item.status === 'out_of_stock';
                    return React.createElement('tr', { 
                      key: item.product_id,
                      className: 'border-b hover:bg-slate-50 ' + (isOutOfStock ? 'bg-red-50' : 'bg-amber-50')
                    },
                      React.createElement('td', { className: 'p-3' },
                        React.createElement('p', { className: 'font-medium' }, item.name),
                        React.createElement('p', { className: 'text-xs text-slate-500 font-mono' }, item.product_id)
                      ),
                      React.createElement('td', { className: 'p-3' },
                        React.createElement('p', null, item.brand || '-'),
                        React.createElement('p', { className: 'text-xs text-slate-500' }, item.category || '-')
                      ),
                      React.createElement('td', { className: 'p-3 text-right font-semibold ' + (isOutOfStock ? 'text-red-600' : 'text-amber-600') },
                        item.current_stock, ' ', item.unit
                      ),
                      React.createElement('td', { className: 'p-3 text-right text-slate-600' }, item.min_stock_level),
                      React.createElement('td', { className: 'p-3 text-right font-semibold text-red-600' }, item.shortage_qty),
                      React.createElement('td', { className: 'p-3 text-right' }, formatCurrency(item.last_purchase_rate)),
                      React.createElement('td', { className: 'p-3 text-right font-semibold text-purple-600' }, formatCurrency(item.reorder_value)),
                      React.createElement('td', { className: 'p-3 text-center' },
                        isOutOfStock 
                          ? React.createElement(Badge, { className: 'bg-red-100 text-red-800' }, 'Out of Stock')
                          : React.createElement(Badge, { className: 'bg-amber-100 text-amber-800' }, 'Low Stock')
                      )
                    );
                  })
                )
              )
            )
      )
    )
  );
}

export default LowStockReport;
